/**
 * One headline figure: what happened, and — only when there is a real
 * previous period to compare against — how it changed.
 */

import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { formatPp } from '../../lib/analytics/metrics';

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  /** Secondary line under the value, e.g. "8 / 10 marks". */
  detail?: ReactNode;
  /** Change in percentage points. Omit when there is no comparable period. */
  deltaPp?: number | null;
  deltaLabel?: string;
  /** How the figure is calculated, shown on hover and to screen readers. */
  definition?: string;
  icon?: ReactNode;
  loading?: boolean;
}

export function MetricCard({ label, value, detail, deltaPp, deltaLabel = 'vs previous period', definition, icon, loading }: MetricCardProps) {
  const hasDelta = typeof deltaPp === 'number' && Number.isFinite(deltaPp);
  const DeltaIcon = !hasDelta || Math.round(deltaPp!) === 0 ? Minus : deltaPp! > 0 ? ArrowUpRight : ArrowDownRight;
  const deltaTone = !hasDelta || Math.round(deltaPp!) === 0 ? 'text-muted-foreground' : deltaPp! > 0 ? 'text-primary' : 'text-destructive';

  return (
    <Card className="rounded-xl border-border bg-card hover:shadow-card" title={definition}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
          {icon && <span className="text-muted-foreground/70" aria-hidden>{icon}</span>}
        </div>
        {loading ? (
          <>
            <Skeleton className="mt-2 h-7 w-20" />
            <Skeleton className="mt-2 h-3 w-24" />
          </>
        ) : (
          <>
            <p className="mt-1.5 text-2xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
            {detail && <p className="mt-0.5 truncate text-xs text-muted-foreground">{detail}</p>}
            {hasDelta && (
              <p className={`mt-1 flex items-center gap-1 text-xs font-medium ${deltaTone}`}>
                <DeltaIcon className="h-3.5 w-3.5" aria-hidden />
                {formatPp(deltaPp)}
                <span className="font-normal text-muted-foreground">{deltaLabel}</span>
              </p>
            )}
          </>
        )}
        {definition && <span className="sr-only">{definition}</span>}
      </CardContent>
    </Card>
  );
}

export function MetricGrid({ children, columns = 6 }: { children: ReactNode; columns?: 4 | 6 }) {
  const wide = columns === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3 xl:grid-cols-6';
  return <div className={`grid grid-cols-2 gap-3 ${wide}`}>{children}</div>;
}
